import React, { useEffect, useState, useRef, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApplications } from '../context/ApplicationContext';
import { MenuIcon, SearchIcon, BellIcon, UserIcon, LogOutIcon, SettingsIcon, HelpCircleIcon } from 'lucide-react';
import NotificationBell from './NotificationBell';
interface UserHeaderProps {
  toggleSidebar: () => void;
  isSidebarOpen: boolean;
  onMobileMenuToggle?: () => void;
  isMobileMenuOpen?: boolean;
}
const UserHeader: React.FC<UserHeaderProps> = ({
  toggleSidebar,
  isSidebarOpen,
  onMobileMenuToggle,
  isMobileMenuOpen
}) => {
  const {
    user,
    logout
  } = useAuth();
  const {
    getApplicationsByUserId
  } = useApplications();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const profileRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Handle clicks outside of profile dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Get unread notifications count
  const unreadNotificationsCount = 1; // This would be dynamic in a real app
  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/track-application?query=${encodeURIComponent(searchQuery)}`);
    }
  };
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  return <header className="bg-white shadow-sm" data-id="element-723">
      <div className="px-4 sm:px-6 lg:px-8 py-4" data-id="element-724">
        <div className="flex justify-between items-center" data-id="element-725">
          <div className="flex items-center" data-id="element-726">
            <button onClick={onMobileMenuToggle} className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500" aria-label="Open mobile menu" data-id="element-727">
              <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-728" />
            </button>
            <div className="flex items-center ml-2 lg:ml-0" data-id="element-729">
              <Link to="/dashboard" className="flex items-center" data-id="element-730">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iLjN9cDQjVbDSGqwWquEQb/1000138367.png" alt="Edo State Logo" className="h-8 w-8 mr-3" data-id="element-731" />
                <h1 className="text-xl font-bold text-gray-900" data-id="element-732">
                  Edo State <span className="text-green-600" data-id="element-733">E-Services</span>
                </h1>
              </Link>
            </div>
          </div>
          <div className="flex items-center space-x-4" data-id="element-734">
            <form onSubmit={handleSearch} className="relative hidden md:block" data-id="element-735">
              <input type="text" placeholder="Search or track application..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" data-id="element-736" />
              <SearchIcon size={20} className="absolute left-3 top-2.5 text-gray-400" data-id="element-737" />
              <button type="submit" className="absolute right-3 top-2.5 text-gray-400 hover:text-green-600" aria-label="Search" data-id="element-738">
                <span className="sr-only" data-id="element-739">Search</span>
              </button>
            </form>
            <Link to="/notifications" data-id="element-740">
              <NotificationBell count={unreadNotificationsCount} data-id="element-741" />
            </Link>
            <div className="relative" ref={profileRef} data-id="element-742">
              <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center focus:outline-none" aria-expanded={isProfileOpen} aria-haspopup="true" data-id="element-743">
                <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center text-white font-medium mr-2" data-id="element-744">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <span className="text-sm font-medium text-gray-700 hidden md:inline-block" data-id="element-745">
                  {user?.name || 'User'}
                </span>
              </button>
              {isProfileOpen && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200" data-id="element-746">
                  <div className="px-4 py-2 border-b border-gray-100" data-id="element-747">
                    <p className="text-sm font-medium text-gray-900" data-id="element-748">
                      {user?.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-749">
                      {user?.email}
                    </p>
                  </div>
                  <Link to="/profile" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsProfileOpen(false)} data-id="element-750">
                    <UserIcon size={16} className="mr-3 text-gray-500" data-id="element-751" />
                    My Profile
                  </Link>
                  <Link to="/notifications" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsProfileOpen(false)} data-id="element-752">
                    <BellIcon size={16} className="mr-3 text-gray-500" data-id="element-753" />
                    Notifications
                    {unreadNotificationsCount > 0 && <span className="ml-auto bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-754">
                        {unreadNotificationsCount}
                      </span>}
                  </Link>
                  <Link to="/support" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsProfileOpen(false)} data-id="element-755">
                    <HelpCircleIcon size={16} className="mr-3 text-gray-500" data-id="element-756" />
                    Help & Support
                  </Link>
                  <div className="border-t border-gray-100 mt-1" data-id="element-757"></div>
                  <button onClick={handleLogout} className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-758">
                    <LogOutIcon size={16} className="mr-3 text-red-500" data-id="element-759" />
                    Logout
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </header>;
};
export default UserHeader;